import React, { useEffect, useState } from "react";
import { emailService } from "../../services/api/email.service";
import { showErrorToast, showSuccessToast } from "../../utils/toast";

interface EmailReportModalProps {
  open: boolean;
  onClose: () => void;
  /** Produces the report PDF as a base64 data-URI string (may be async). */
  getAttachmentBase64: () => string | Promise<string>;
  /** File name used for the PDF attachment (e.g. "applications-summary-report.pdf"). */
  fileName: string;
  /** Default subject line shown when the dialog opens. */
  defaultSubject: string;
  /** Default message body shown when the dialog opens. */
  defaultMessage?: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Reusable dialog that emails a generated PDF report to one or more recipients.
 */
const EmailReportModal: React.FC<EmailReportModalProps> = ({
  open,
  onClose,
  getAttachmentBase64,
  fileName,
  defaultSubject,
  defaultMessage = "Please find the attached report.",
}) => {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);

  // Reset the subject/message each time the dialog is opened.
  useEffect(() => {
    if (open) {
      setSubject(defaultSubject);
      setMessage(defaultMessage);
    }
  }, [open, defaultSubject, defaultMessage]);

  if (!open) return null;

  const handleClose = () => {
    if (sending) return;
    onClose();
  };

  const handleSend = async () => {
    const recipients = to.trim();
    if (!recipients) {
      showErrorToast("Please enter at least one recipient email address.");
      return;
    }
    const parts = recipients
      .split(/[,;]/)
      .map((p) => p.trim())
      .filter(Boolean);
    const invalid = parts.find((p) => !EMAIL_PATTERN.test(p));
    if (invalid) {
      showErrorToast(`"${invalid}" is not a valid email address.`);
      return;
    }

    setSending(true);
    try {
      const attachmentBase64 = await getAttachmentBase64();
      await emailService.sendReport({
        to: parts.join(","),
        subject: subject.trim() || defaultSubject,
        message: message.trim(),
        attachmentBase64,
        attachmentFileName: fileName,
        attachmentContentType: "application/pdf",
      });
      showSuccessToast("Report emailed successfully.");
      onClose();
      setTo("");
    } catch (err) {
      showErrorToast(
        err instanceof Error ? err.message : "Failed to email the report.",
      );
    } finally {
      setSending(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    fontSize: "14px",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
        padding: "20px",
      }}
      onClick={handleClose}
    >
      <div
        style={{
          width: "min(520px, 96vw)",
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <strong>✉️ Email Report</strong>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={handleClose}
            disabled={sending}
            style={{ padding: "4px 12px" }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: "20px", display: "grid", gap: "14px" }}>
          <label style={{ display: "grid", gap: "6px" }}>
            <span style={{ fontSize: "13px", fontWeight: 600 }}>
              To <span style={{ color: "#dc3545" }}>*</span>
            </span>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="name@example.com, another@example.com"
              disabled={sending}
              style={inputStyle}
            />
            <span style={{ fontSize: "12px", color: "#8a9ab5" }}>
              Separate multiple addresses with a comma.
            </span>
          </label>

          <label style={{ display: "grid", gap: "6px" }}>
            <span style={{ fontSize: "13px", fontWeight: 600 }}>Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
              style={inputStyle}
            />
          </label>

          <label style={{ display: "grid", gap: "6px" }}>
            <span style={{ fontSize: "13px", fontWeight: 600 }}>Message</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              disabled={sending}
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </label>

          <div
            style={{
              fontSize: "13px",
              color: "#475569",
              background: "#f1f5f9",
              borderRadius: "6px",
              padding: "10px 12px",
            }}
          >
            📎 The report will be attached as a PDF.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            padding: "16px 20px",
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <button
            type="button"
            className="btn btn--secondary"
            onClick={handleClose}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? "Sending…" : "Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailReportModal;
